<template>
    <div class="mt-4">
        <!-- PHARMACY INFO -->
        <ValidationObserver v-slot="{ handleSubmit, invalid }">
            <form @submit.prevent="handleSubmit(handleSubmitClick)">
                <fieldset :disabled="!user.permissions.edit_pharmacy">
                    <div class="row">
                        <div class="col-sm-6 col-md-6 col-lg-5 col-xl-4">
                            <div class="form-group">
                                <ValidationProvider name="Name" rules="required" v-slot="{ errors }">
                                    <label for="name">{{ $t('name') }}</label>
                                    <input
                                        type="text"
                                        class="form-control"
                                        v-model="pharmacy.information.name"
                                        id="name"
                                    />
                                    <div class="invalid-feedback" style="display: block">
                                        <span>{{ $t(errors[0]) }}</span>
                                    </div>
                                </ValidationProvider>
                            </div>
                            <div class="form-group">
                                <ValidationProvider name="Country" rules="required" v-slot="{ errors }">
                                    <label for="country">{{ $t('country') }}</label>
                                    <input
                                        type="text"
                                        class="form-control"
                                        disabled
                                        id="country"
                                        v-model="pharmacy.information.country"
                                    />
                                    <div class="invalid-feedback" style="display: block">
                                        <span>{{ $t(errors[0]) }}</span>
                                    </div>
                                </ValidationProvider>
                            </div>
                            <div class="form-group">
                                <label for="street">{{ $t('street') }}</label>
                                <input
                                    type="text"
                                    class="form-control"
                                    id="street"
                                    v-model="pharmacy.information.street"
                                />
                            </div>
                        </div>
                        <div class="col-sm-6 col-md-6 col-lg-5 col-xl-4">
                            <div class="form-group">
                                <label for="business_id">{{ $t('business_id') }}</label>
                                <input
                                    type="text"
                                    class="form-control"
                                    id="business_id"
                                    v-model="pharmacy.information.business_id"
                                />
                            </div>
                            <div class="form-group">
                                <ValidationProvider name="City" rules="required" v-slot="{ errors }">
                                    <label for="city">{{ $t('city') }}</label>
                                    <input
                                        type="text"
                                        class="form-control"
                                        id="city"
                                        v-model="pharmacy.information.city"
                                    />
                                    <div class="invalid-feedback" style="display: block">
                                        <span>{{ $t(errors[0]) }}</span>
                                    </div>
                                </ValidationProvider>
                            </div>
                            <div class="form-group">
                                <label for="zip">{{ $t('zip') }}</label>
                                <input type="text" class="form-control" id="zip" v-model="pharmacy.information.zip" />
                            </div>
                            <div class="form-group">
                                <label for="description">{{ $t('description') }}</label>
                                <textarea
                                    class="form-control"
                                    id="description"
                                    v-model="pharmacy.information.description"
                                ></textarea>
                            </div>
                        </div>
                    </div>
                    <hr />
                    <div class="row">
                        <div class="col-sm-6 col-md-6 col-lg-5 col-xl-4">
                            <div class="form-group">
                                <label for="alarm_phone">{{ $t('alarm_phone') }}</label>
                                <input
                                    type="text"
                                    class="form-control"
                                    id="alarm_phone"
                                    v-model="pharmacy.information.alarm_phone"
                                />
                            </div>
                        </div>
                        <div class="col-sm-6 col-md-6 col-lg-5 col-xl-4">
                            <div class="form-group">
                                <label for="alarm_email">{{ $t('alarm_email') }}</label>
                                <input
                                    class="form-control"
                                    id="alarm_email"
                                    v-model="pharmacy.information.alarm_email"
                                />
                            </div>
                        </div>
                    </div>
                    <hr />
                    <!-- EXPIRY ALERT -->
                    <div class="row">
                        <div class="col-sm-12 mt-1 mb-2">
                            <h6>{{ $t('expiry_alerts') }}</h6>
                        </div>
                    </div>
                    <div class="row">
                        <!-- EXPIRY ALERT RECIPIENT -->
                        <div class="col-sm-6 col-md-6 col-lg-5 col-xl-4">
                            <ValidationProvider
                                name="expiryAlertRecipientVal"
                                v-slot="{ errors }"
                                rules="required_if:expiryAlertTimeValueVal"
                                vid="expiryAlertRecipientVal"
                            >
                                <label
                                    for="expiryAlertRecipientSelection"
                                    v-b-tooltip.hover
                                    :title="$t('expiry_alert_recipient_tooltip')"
                                >
                                    {{ $t('recipient') }}
                                </label>
                                <b-form-select
                                    id="expiryAlertRecipientSelection"
                                    :options="pharmacyWebUserOptions"
                                    v-model="pharmacy.config.expiry_alert.recipient"
                                />
                                <div class="invalid-feedback" style="display: block">
                                    <span>
                                        {{
                                            errors[0] === 'error_field_required'
                                                ? $t('error_field_required_if_alert_time_specified')
                                                : $t(errors[0])
                                        }}
                                    </span>
                                </div>
                            </ValidationProvider>
                        </div>
                        <div class="col-sm-6 col-md-6 col-lg-5 col-xl-4">
                            <label
                                for="expiryAlertTimeValueVal"
                                v-b-tooltip.hover
                                :title="$t('expiry_alert_time_tooltip')"
                            >
                                {{ $t('alert_time') }}
                            </label>
                            <ValidationProvider
                                name="expiryAlertTimeValueVal"
                                v-slot="{ errors }"
                                :rules="{
                                    integer: true,
                                    positive: true,
                                    required_if: 'expiryAlertRecipientVal'
                                }"
                                vid="expiryAlertTimeValueVal"
                                id="expiryAlertTimeValueVal"
                            >
                                <div class="row">
                                    <!-- EXPIRY ALERT TIME VALUE -->
                                    <div class="col-sm-5 col-md-5 col-lg-4">
                                        <input
                                            class="form-control"
                                            id="expiryAlertTimeValueInput"
                                            v-model="pharmacy.config.expiry_alert.time_value"
                                        />
                                    </div>
                                    <!-- EXPIRY ALERT TIME UNIT -->
                                    <div class="col-sm-7 col-md-7 col-lg-8" :style="{ paddingLeft: 0 }">
                                        <b-form-select
                                            id="expiryAlertTimeUnitSelection"
                                            :options="alertTimeUnitOptions"
                                            v-model="pharmacy.config.expiry_alert.time_unit"
                                        />
                                    </div>
                                </div>
                                <div class="invalid-feedback" style="display: block">
                                    <span>
                                        {{
                                            errors[0] === 'error_field_required'
                                                ? $t('error_field_required_if_recipient_specified')
                                                : $t(errors[0])
                                        }}
                                    </span>
                                </div>
                            </ValidationProvider>
                        </div>
                    </div>
                    <hr />
                    <!-- LOW INVENTORY ALERT -->
                    <div class="row">
                        <div class="col-sm-12 mt-1 mb-2">
                            <h6>{{ $t('low_inventory_alerts') }}</h6>
                        </div>
                    </div>
                    <div class="row">
                        <!-- LOW INVENTORY ALERT RECIPIENT -->
                        <div class="col-sm-6 col-md-6 col-lg-5 col-xl-4">
                            <ValidationProvider
                                name="lowInventoryAlertRecipient"
                                rules="required_if:lowInventoryAlertThreshold"
                                v-slot="{ errors }"
                                vid="lowInventoryAlertRecipient"
                            >
                                <label
                                    for="lowInventoryAlertRecipientSelection"
                                    v-b-tooltip.hover
                                    :title="$t('low_inventory_alert_recipient_tooltip')"
                                >
                                    {{ $t('recipient') }}
                                </label>
                                <b-form-select
                                    id="lowInventoryAlertRecipientSelection"
                                    :options="pharmacyWebUserOptions"
                                    v-model="pharmacy.config.low_inventory_alert.recipient"
                                />
                                <div class="invalid-feedback" style="display: block">
                                    <span>
                                        {{
                                            errors[0] === 'error_field_required'
                                                ? $t('error_field_required_if_alert_threshold_specified')
                                                : $t(errors[0])
                                        }}
                                    </span>
                                </div>
                            </ValidationProvider>
                        </div>
                        <!-- LOW INVENTORY ALERT THRESHOLD VALUE AND UNIT -->
                        <div class="col-sm-6 col-md-6 col-lg-5 col-xl-4">
                            <label
                                for="lowInventoryAlertTimeValue"
                                v-b-tooltip.hover
                                :title="$t('low_inventory_alert_threshold_tooltip_default')"
                            >
                                {{ $t('alert_threshold') }}
                            </label>
                            <ValidationProvider
                                name="lowInventoryAlertThreshold"
                                :rules="{
                                    integer: true,
                                    positive: true
                                }"
                                v-slot="{ errors }"
                                vid="lowInventoryAlertThreshold"
                            >
                                <div class="row align-items-center">
                                    <!-- LOW INVENTORY ALERT THRESHOLD VALUE -->
                                    <div class="col-sm-5 col-md-5 col-lg-4">
                                        <input
                                            class="form-control"
                                            id="lowInventoryAlertThresholdInput"
                                            v-model="pharmacy.config.low_inventory_alert.amount_value"
                                        />
                                    </div>
                                    <!-- LOW INVENTORY ALERT THRESHOLD UNIT -->
                                    <div class="col-sm-7 col-md-7 col-lg-8" :style="{ paddingLeft: '0px' }">
                                        <input
                                            class="form-control"
                                            disabled
                                            :value="$t('packages_long_singular_partitive').toLowerCase()"
                                        />
                                    </div>
                                </div>
                                <div class="invalid-feedback" style="display: block">
                                    <span>
                                        {{ $t(errors[0]) }}
                                    </span>
                                </div>
                            </ValidationProvider>
                        </div>
                    </div>
                    <hr />


                    <!-- Timeout of the drugsafe box -->
                    <!--<div class="row">
                        <div class="col-sm-12 mt-1 mb-2">
                            <h6>{{ $t('session_length') }}</h6>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-sm-6 col-md-6 col-lg-5 col-xl-4">
                            <ValidationProvider
                                name="drugsafeSessionLength"
                                :rules="{
                                    integer: true,
                                    maxValue: { maxValue: sessionBoxLengthMax },
                                    minValue: { minValue: sessionBoxLengthMin }
                                }"
                                v-slot="{ errors }"
                                vid="drugsafeSessionLength"
                            >
                                <div class="input-group">
                                    <input
                                        type="number"
                                        :min="sessionBoxLengthMin"
                                        :max="sessionBoxLengthMax"
                                        class="form-control"
                                        v-model="pharmacy.config.session_length"
                                        id="drugsafeSessionLengthInput"
                                    />
                                    <div class="input-group-append">
                                        <span class="input-group-text">{{ $t('minutes_partitive') }}</span>
                                    </div>
                                </div>
                                <div class="invalid-feedback" style="display: block">
                                <span>
                                    {{ $t(errors[0]) }}
                                    {{ errors[0] === 'error_value_too_high' ? sessionBoxLengthMax : '' }}
                                    {{ errors[0] === 'error_value_too_low' ? sessionBoxLengthMin : '' }}
                                </span>
                                </div>
                            </ValidationProvider>
                        </div>
                    </div>
                    <hr />-->
                    <div class="row mt-4">
                        <div class="col-sm-6">
                            <div class="d-flex">
                                <label
                                    for="validationSwitch"
                                    v-b-tooltip.hover
                                    :title="$t('mandatory_inventory_check_tooltip')"
                                >
                                    {{ $t('mandatory_inventory_check') }}
                                </label>
                                <b-form-checkbox
                                    id="validationSwitch"
                                    class="ml-2"
                                    v-model="pharmacy.config.mandatory_inventory_check"
                                    switch
                                />
                            </div>
                        </div>
                    </div>
                    <div class="row mt-3">
                        <div class="col-sm-6">
                            <div class="d-flex">
                                <label
                                    for="defaultLockerSwitch"
                                    v-b-tooltip.hover
                                    :title="$t('default_lockers_tooltip')"
                                >
                                    {{ $t('default_lockers') }}
                                </label>
                                <b-form-checkbox
                                    id="defaultLockerSwitch"
                                    class="ml-2"
                                    v-model="pharmacy.config.default_vnr_locker_setup"
                                    switch
                                />
                            </div>
                        </div>
                    </div>
                    <div class="row mt-3">
                        <div class="col-sm-6">
                            <div class="d-flex">
                                <label
                                    for="deleteOldLogsSwitch"
                                    v-b-tooltip.hover
                                    :title="$t('delete_old_logs_tooltip')"
                                >
                                    {{ $t('delete_old_logs') }}
                                </label>
                                <b-form-checkbox
                                    id="deleteOldLogsSwitch"
                                    class="ml-2"
                                    v-model="pharmacy.config.delete_old_logs"
                                    switch
                                />
                            </div>
                        </div>
                    </div>
                    <div v-if="user.permissions.edit_pharmacy">
                        <hr />
                        <div class="row mt-4">
                            <div class="col-sm-4">
                                <input
                                    type="submit"
                                    class="btn btn-success"
                                    :value="$t('save')"
                                    :disabled="submitClicked || invalid"
                                />
                            </div>
                        </div>
                    </div>
                </fieldset>
            </form>
        </ValidationObserver>
        <!-- MODAL: UPDATE DRUG CARD 'SHOW INV CHECK COL' SETTING? -->
        <b-modal
            id="updateShowDrugCardInvCheckColModal"
            centered
            :title="
                this.pharmacy.config.mandatory_inventory_check
                    ? $t('update_drug_card_inv_check_col_show_title')
                    : $t('update_drug_card_inv_check_col_hide_title')
            "
            hide-footer
        >
            <div class="row">
                <div class="col">
                    <p>
                        {{
                            this.pharmacy.config.mandatory_inventory_check
                                ? $t('update_drug_card_inv_check_col_show_text')
                                : $t('update_drug_card_inv_check_col_hide_text')
                        }}
                    </p>
                    <p>{{ $t('update_drug_card_inv_check_col_common_text') }}</p>
                </div>
            </div>
            <div class="d-flex row">
                <div class="d-flex col justify-content-center">
                    <button class="btn btn-primary mr-4" @click="updateDrugCardLayoutAndSubmit">
                        {{ $t('yes') }}
                    </button>
                    <button class="btn btn-secondary" @click="handleSubmitClick(true)">
                        {{ $t('no') }}
                    </button>
                </div>
            </div>
        </b-modal>
        <!-- MODAL: ARE YOU SURE YOU WANT TO DELETE OLD LOGS? -->
        <b-modal id="submitConfirmationModal" centered :title="$t('delete_old_logs_are_you_sure_title')" hide-footer>
            <div class="row">
                <div class="col">
                    <p>{{ $t('delete_old_logs_are_you_sure_text') }}</p>
                </div>
            </div>
            <div class="d-flex row mt-2">
                <div class="d-flex col justify-content-center">
                    <button class="btn btn-primary mr-4" @click="submit">
                        {{ $t('ok') }}
                    </button>
                    <button class="btn btn-secondary" @click="handleConfirmationCancel">
                        {{ $t('cancel') }}
                    </button>
                </div>
            </div>
        </b-modal>
    </div>
</template>

<script>
import { mapState } from 'vuex';
import { updatePharmacy, handleAxiosError } from '@/services/axiosService';

export default {
    props: {
        pharmacy: {
            type: Object,
            required: true
        },
        pharmacyWebUsers: {
            validator: prop => typeof prop === 'object' || prop === null,
            required: true
        }
    },
    data() {
        return {
            errors: [],
            deleteOldLogsInitial: this.pharmacy.config.delete_old_logs,
            showDrugCardInvCheckColInitial: this.pharmacy.config.drug_card_layout.show_inventory_check_column,
            submitClicked: false,
            sessionBoxLengthMax: 60,
            sessionBoxLengthMin: 3
        };
    },
    created() {
        // initialize alert fields if not set
        this.pharmacy.config = {
            ...this.pharmacy.config,
            ...(!this.pharmacy.config.expiry_alert && {
                expiry_alert: {
                    recipient: null,
                    time_unit: 'days',
                    time_value: null
                }
            }),
            ...(!this.pharmacy.config.low_inventory_alert && {
                low_inventory_alert: {
                    amount_value: null,
                    recipient: null
                }
            })
        };
    },
    computed: {
        ...mapState('account', ['status', 'user']),
        alertTimeUnitOptions() {
            return [
                { value: 'days', text: this.$t('time_period_selection_days') },
                { value: 'weeks', text: this.$t('time_period_selection_weeks') },
                { value: 'months', text: this.$t('time_period_selection_months') }
            ];
        },
        pharmacyWebUserOptions() {
            let pharmacyWebUserOptions = _.sortBy(this.pharmacyWebUsers, ['last_name', 'first_name']);
            pharmacyWebUserOptions = pharmacyWebUserOptions.map(user => {
                return { value: user.id, text: `${user.first_name} ${user.last_name}` };
            });
            pharmacyWebUserOptions = [{ value: null, text: this.$t('no_selection') }, ...pharmacyWebUserOptions];
            return pharmacyWebUserOptions;
        }
    },
    methods: {
        // handle confirmation cancel action
        handleConfirmationCancel() {
            this.submitClicked = false;
            this.$bvModal.hide('submitConfirmationModal');
        },
        // handle submit click
        async handleSubmitClick(skipInvCheckMatchCheck) {
            this.submitClicked = true;
            if (
                !skipInvCheckMatchCheck &&
                this.showDrugCardInvCheckColInitial !== this.pharmacy.config.mandatory_inventory_check
            ) {
                // if inventory check setting changed, show confirmation modal
                this.$bvModal.show('updateShowDrugCardInvCheckColModal');
            } else {
                this.$bvModal.hide('updateShowDrugCardInvCheckColModal');
                if (!this.deleteOldLogsInitial && this.pharmacy.config.delete_old_logs) {
                    // if "delete old logs" has been turned on, show confirmation modal
                    this.$bvModal.show('submitConfirmationModal');
                } else {
                    // otherwise submit form directly
                    await this.submit();
                }
            }
        },
        // submit form (called only after any confirmation modals ok'd)
        async submit() {
            try {
                this.$bvModal.hide('submitConfirmationModal');
                // format alert config values
                const config = { ...this.pharmacy.config };
                if (!config.expiry_alert.recipient) {
                    delete config.expiry_alert;
                } else {
                    config.expiry_alert.time_value = parseInt(config.expiry_alert.time_value);
                }
                if (!config.low_inventory_alert.recipient) {
                    delete config.low_inventory_alert;
                } else {
                    const lowInvAlertValue = parseInt(config.low_inventory_alert.amount_value);
                    config.low_inventory_alert.amount_unit = lowInvAlertValue ? 'packages' : null;
                    config.low_inventory_alert.amount_value = lowInvAlertValue ? lowInvAlertValue : null;
                }
                if (config.session_length) {
                    config.session_length = parseInt(config.session_length);
                } else {
                    config.session_length = this.sessionBoxLengthMin;
                }

                await updatePharmacy(this.pharmacy.id, { ...this.pharmacy, config });
                this.$toastr.s(this.$t('pharmacy_updated'));
                // update "initial" values to match updated values
                this.deleteOldLogsInitial = this.pharmacy.config.delete_old_logs;
                this.showDrugCardInvCheckColInitial = this.pharmacy.config.drug_card_layout.show_inventory_check_column;
            } catch (error) {
                handleAxiosError(error);
            }
            this.submitClicked = false;
        },
        // handle "update drug card layout?"" modal "ok" click (update pharmacy config and submit form)
        async updateDrugCardLayoutAndSubmit() {
            this.pharmacy.config.drug_card_layout.show_inventory_check_column = !this.pharmacy.config.drug_card_layout
                .show_inventory_check_column;
            this.handleSubmitClick(true);
        }
    },
    // handle other "hide modal" actions (background click, esc key press, modal close click)
    mounted() {
        this.$root.$on('bv::modal::hide', bvEvent => {
            if (['backdrop', 'esc', 'headerclose'].includes(bvEvent.trigger)) {
                this.submitClicked = false;
            }
        });
    }
};
</script>
